// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { constants } from "@stanion/XDL/constants";
import { hideInputNumberArrows, maxLengthAttr, mergeWithCss, xMask, xScreen } from "@stanion/XDL/Utilities";
import color from "@stanion/XDL/Utilities/color";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    // XNG-Changes
    showActions?: boolean;
    showLabel?: boolean;
    showShippedQty?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;
// XNG-Changes
export interface CartLineQuantityActionsStyles {
    wrapper?: InjectableCss;
    actionClickable?: ClickableProps;
    quantityOrderedTextField?: TextFieldPresentationProps;
}
export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    // XNG-Change
    actionsStyles?: CartLineQuantityActionsStyles;
    disabledTextField?: TextFieldPresentationProps;
    readOnlyQtyLabel?: TypographyPresentationProps;
    readOnlyQtyValue?: TypographyPresentationProps;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: 10px;
                text-transform: uppercase;
                font-weight: 600;
                height: 28px;
                ${xScreen("lte", "md")} {
                    text-align: center;
                    display: block;
                    min-height: auto;
                }
            `,
        },
    },
    actionsStyles: {
        wrapper: {
            css: css`
                background: ${color("common.white")};
                border: 1px solid ${color("common.ui5")};
                display: flex;
                align-items: center;
                height: 30px;
                max-width: 80px;
            `,
        },
        actionClickable: {
            css: css`
                height: 26px;
                padding: 0 8px;
                display: flex;
                align-items: center;
                transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
                ${IconWrapper} {
                    color: ${color("common.ui5")};
                }
                &:hover {
                    ${IconWrapper} {
                        color: ${color("primary")};
                    }
                }
                &:focus {
                    outline: none;
                }
            `,
        },
        quantityOrderedTextField: {
            cssOverrides: {
                inputSelect: css`
                    height: 26px;
                    border: none;
                    box-shadow: none !important;
                    font-size: 14px;
                    letter-spacing: 0;
                    line-height: 16px;
                    text-align: center;
                    padding: 0 !important;
                    ${hideInputNumberArrows}
                    &:hover,
                    &:focus {
                        border: none;
                        box-shadow: none !important;
                    }
                `,
            },
        },
    },
    disabledTextField: {
        cssOverrides: {
            formField: css`
                max-width: 60px;
            `,
            inputSelect: css`
                width: 57px;
                height: 28px;
                text-align: center;
                border: 1px solid ${color("common.ui6")};
                background: ${color("common.ui14")};
                &:disabled {
                    color: ${color("text.main")};
                    background: ${color("common.ui4")};
                }
                &:hover {
                    border: 1px solid ${color("common.ui6")};
                }
            `,
        },
    },
    readOnlyQtyLabel: {
        size: 12,
        lineHeight: "13px",
        weight: "normal",
        css: css`
            text-transform: capitalize;
            padding: 0;
            margin-bottom: 8px;
            display: block;
        `,
    },
    readOnlyQtyValue: {
        variant: "h6",
        lineHeight: "17px",
        css: css`
            margin-bottom: 16px;
        `,
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    showActions,
    showLabel,
    showShippedQty,
}) => {
    const localOnQtyOrderedChange = (value: string) => {
        // XNg-Change: apply mask
        const inputQty = Number(xMask.qty(value));
        if (Number.isNaN(inputQty)) {
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );
    // <XNG-Changes>
    const textFieldId = `cartLine-qty-orderd-input-${cartLine.id}-${uniqueId()}`;
    const qtyInputProps = {
        label,
        value,
        type: "number",
        min: 0,
        max: 999999,
        "data-test-selector": "cartline_qty",
        onClick: (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => event.currentTarget.focus(),
        onChange: changeHandler,
        onKeyDown: keyDownHandler,
        onBlur: blurHandler,
        disabled: !cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id],
        id: textFieldId,
        maxLength: maxLengthAttr.qty,
    };
    const updateQtyByAction = (type: "increase" | "decrease") => {
        const event = { currentTarget: { value: "" } };
        event.currentTarget.value = `${Number(value) + (type === "increase" ? 1 : -1)}`;
        localOnQtyOrderedChange(`${Number(value) + (type === "increase" ? 1 : -1)}`);
    };
    // </XNG-Changes>
    const [styles] = React.useState(() => mergeWithCss(cartLineQuantityStyles, extendedStyles));
    // <XNG-Changes>
    if (showActions) {
        return (
            <StyledWrapper {...styles?.actionsStyles?.wrapper}>
                <Clickable
                    {...styles?.actionsStyles?.actionClickable}
                    disabled={Number(value) <= 1}
                    onClick={() => updateQtyByAction("decrease")}
                    data-test-selector="product_qtyOrdered_decrease"
                    aria-label="decrease quantity"
                >
                    <Icon src={Minus} size={10} />
                </Clickable>
                <VisuallyHidden as="label" htmlFor={textFieldId}>
                    {translate("Qty")}
                </VisuallyHidden>
                <TextField {...qtyInputProps} {...styles?.actionsStyles?.quantityOrderedTextField} />
                <Clickable
                    {...styles?.actionsStyles?.actionClickable}
                    disabled={Number(value) > 999999}
                    onClick={() => updateQtyByAction("increase")}
                    data-test-selector="product_qtyOrdered_increase"
                    aria-label="increase quantity"
                >
                    <Icon src={Plus} size={10} />
                </Clickable>
            </StyledWrapper>
        );
    }
    // </XNG-Changes>
    if (editable && !isOutOfStock(cartLine)) {
        return <TextField {...styles.editableQuantityTextField} {...qtyInputProps} />;
    }
    // editable = false
    return (
        <>
            <VisuallyHidden as="label" htmlFor={textFieldId}>
                {translate("Qty")}
            </VisuallyHidden>
            {showShippedQty && <Typography {...styles.readOnlyQtyLabel}>{translate("Ordered")}</Typography>}

            <Typography {...styles.readOnlyQtyValue} as="p" id={textFieldId}>
                {cartLine.qtyOrdered}
            </Typography>
            {showShippedQty && (
                <>
                    <Typography {...styles.readOnlyQtyLabel}>{translate("Shipped")}</Typography>
                    <Typography {...styles.readOnlyQtyValue}>{+cartLine.properties?.qtyShipped}</Typography>
                </>
            )}
        </>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
